// features/admin/referFriendApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ReferralUser {
  user_id: number;
  full_name: string;
  email: string;
  referral_code: string;
  joined_date: string;
  total_referrals: number;
  successful_referrals: number;
  total_reward_earned: string | number;
}

export interface ReferralTransaction {
  id: number;
  referrer_id: number;
  referrer_name: string;
  referrer_email: string;
  referred_id: number;
  referred_name: string;
  referred_email: string;
  referral_code: string;
  reward_amount: string | number;
  status: 'pending' | 'completed' | 'failed';
  order_id: number | null;
  created_at: string;
}

export interface ReferralListResponse {
  error: boolean;
  message?: string;
  stats: {
    total_referrals: number;
    successful: number;
    total_rewards: number;
  };
  count: number;
  data: ReferralUser[];
}

export interface ReferralTransactionsResponse {
  error: boolean;
  total: number;
  pending_count: number;
  page: number;
  limit: number;
  data: ReferralTransaction[];
}

export interface ListReferralsRequest {
  search?: string;
}

export interface ListReferralTransactionsRequest {
  referrer_id?: number;
  status?: 'pending' | 'completed' | 'failed' | '';
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

export interface UpdateReferralStatusRequest {
  id: number;
  status: 'pending' | 'completed' | 'failed';
}

export interface UpdateReferralStatusResponse {
  error: boolean;
  message: string;
}

// ─── New Interfaces for Referral Settings ────────────────────────────────────

export interface ReferralSetting {
  id: number;
  reward_amount: string;
  reward_points: number;
  hold_days?: number; // days after delivery before the points are given
  is_default: number;
  created_at: string;
  updated_at: string;
}

export interface ReferralSettingsListResponse {
  error: boolean;
  data: ReferralSetting[];
}

export interface SaveReferralSettingRequest {
  id?: number;
  reward_amount?: number;
  reward_points?: number;
  hold_days?: number;
  is_default?: number;
}

export interface SaveReferralSettingResponse {
  error: boolean;
  message: string;
  id?: number;
}

export interface DeleteReferralSettingRequest {
  id: number;
}

export interface DeleteReferralSettingResponse {
  error: boolean;
  message: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const REFERRAL_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT,
  ENDPOINTS: {
    LIST_REFERRALS:              '/admin/list-referrals',
    LIST_REFERRAL_TRANSACTIONS:  '/admin/list-referral-transactions',
    UPDATE_REFERRAL_STATUS:      '/admin/update-referral-status',
    REFERRAL_SETTINGS_LIST:      '/admin/referralsettings-list',
    REFERRAL_SETTINGS_SAVE:      '/admin/referralsettings-save',
    REFERRAL_SETTINGS_DELETE:    '/admin/referralsettings-delete',
  },
};

// ─── API Slice ────────────────────────────────────────────────────────────────

export const referFriendApi = createApi({
  reducerPath: 'referFriendApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Referrals', 'ReferralTransactions', 'ReferralSettings'],
  endpoints: (build) => ({

    // ── List all referrers with stats ──
    listReferrals: build.query<ReferralListResponse, ListReferralsRequest | void>({
      query: (params) => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.LIST_REFERRALS,
        method: 'POST',
        body: { search: params?.search || '' },
      }),
      providesTags: ['Referrals'],
      transformResponse: (response: any): ReferralListResponse => ({
        error:  response?.error  ?? false,
        message: response?.message || '',
        stats: response?.stats || { total_referrals: 0, successful: 0, total_rewards: 0 },
        count: response?.count || 0,
        data:  response?.data  || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || 'Failed to load referrals',
        stats: { total_referrals: 0, successful: 0, total_rewards: 0 },
        count: 0,
        data: [],
      }),
    }),

    // ── List referral transactions ──
    listReferralTransactions: build.query<ReferralTransactionsResponse, ListReferralTransactionsRequest>({
      query: (params) => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.LIST_REFERRAL_TRANSACTIONS,
        method: 'POST',
        body: {
          referrer_id: params.referrer_id || undefined,
          status:      params.status      || undefined,
          from_date:   params.from_date   || undefined,
          to_date:     params.to_date     || undefined,
          page:        params.page        || 1,
          limit:       params.limit       || 20,
        },
      }),
      providesTags: (_result, _error, { referrer_id }) =>
        referrer_id
          ? [{ type: 'ReferralTransactions' as const, id: referrer_id }]
          : ['ReferralTransactions'],
      transformResponse: (response: any): ReferralTransactionsResponse => ({
        error:         response?.error         ?? false,
        total:         response?.total         || 0,
        pending_count: response?.pending_count || 0,
        page:          response?.page          || 1,
        limit:         response?.limit         || 20,
        data:          response?.data          || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || 'Failed to load referral transactions',
        total: 0, pending_count: 0, page: 1, limit: 20, data: [],
      }),
    }),

    // ── Update referral status ──
    updateReferralStatus: build.mutation<UpdateReferralStatusResponse, UpdateReferralStatusRequest>({
      query: (body) => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.UPDATE_REFERRAL_STATUS,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Referrals', 'ReferralTransactions'],
      transformResponse: (response: any): UpdateReferralStatusResponse => ({
        error:   response?.error   ?? false,
        message: response?.message || 'Status updated successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || 'Failed to update referral status',
      }),
    }),

    // ── NEW: Get referral settings list ──
    listReferralSettings: build.query<ReferralSettingsListResponse, void>({
      query: () => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.REFERRAL_SETTINGS_LIST,
        method: 'GET',
      }),
      providesTags: ['ReferralSettings'],
      transformResponse: (response: any): ReferralSettingsListResponse => ({
        error: response?.error ?? false,
        data: response?.data || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        data: [],
      }),
    }),

    // ── NEW: Save referral setting (add or update) ──
    saveReferralSetting: build.mutation<SaveReferralSettingResponse, SaveReferralSettingRequest>({
      query: (body) => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.REFERRAL_SETTINGS_SAVE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReferralSettings'],
      transformResponse: (response: any): SaveReferralSettingResponse => ({
        error:   response?.error   ?? false,
        message: response?.message || 'Setting saved successfully',
        id:      response?.id,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || 'Failed to save referral setting',
      }),
    }),

    // ── NEW: Delete referral setting ──
    deleteReferralSetting: build.mutation<DeleteReferralSettingResponse, DeleteReferralSettingRequest>({
      query: (body) => ({
        url: REFERRAL_API_CONFIG.ENDPOINTS.REFERRAL_SETTINGS_DELETE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReferralSettings'],
      transformResponse: (response: any): DeleteReferralSettingResponse => ({
        error:   response?.error   ?? false,
        message: response?.message || 'Setting deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || 'Failed to delete referral setting',
      }),
    }),

  }),
});

export const {
  useListReferralsQuery,
  useListReferralTransactionsQuery,
  useUpdateReferralStatusMutation,
  useListReferralSettingsQuery,
  useSaveReferralSettingMutation,
  useDeleteReferralSettingMutation,
} = referFriendApi;